// lib/sync/restore.ts — Restore data from Drive backup on app launch
//
// Strategy: "Last-Write-Wins (LWW) CRDT"
//   - Compare updatedAt timestamp for each record.
//   - Cloud record overwrites local only if it's newer.
//   - Soft-deleted records (tombstones) are merged normally.
//   - pinState, settings, syncLog are never touched.

import { downloadBackup } from './api-client';
import { decryptData } from '../crypto';
import { getDB } from '../db/schema';
import type { Group, AllocationHistory, MonthlySnapshot } from '../../types';

export interface RestoreResult {
  restored: boolean;
  recordCount: number;
  backupPath?: string;
}

export interface BackupPayload {
  groups: Group[];
  transactions: import('../../types').Transaction[];
  snapshots: MonthlySnapshot[];
  history: AllocationHistory[];
  syncLogs?: unknown[]; // intentionally ignored
  exportedAt?: string;
}

/**
 * Import records from a decrypted backup payload into IndexedDB.
 * Uses Last-Write-Wins (LWW) by comparing timestamps.
 * pinState, settings, and syncLog are not touched.
 */
export async function importAllData(userId: string, data: BackupPayload): Promise<number> {
  const db = await getDB();
  let count = 0;

  const mergeRecord = async (storeName: 'groups' | 'transactions' | 'allocationHistory' | 'monthlySnapshots', record: any, key: any) => {
    const local = await db.get(storeName, key) as any;
    const remoteTime = new Date(record.updatedAt || record.calculatedAt || record.changedAt || 0).getTime();
    const localTime = local ? new Date(local.updatedAt || local.calculatedAt || local.changedAt || 0).getTime() : -1;
    if (!local || remoteTime > localTime) {
      await db.put(storeName, record);
      count++;
    }
  };

  // Groups — filter to this user for safety
  const groups = (data.groups ?? []).filter(g => g.userId === userId);
  for (const g of groups) {
    await mergeRecord('groups', g, g.groupId);
  }

  // Transactions
  const transactions = (data.transactions ?? []).filter(t => t.userId === userId);
  for (const t of transactions) {
    await mergeRecord('transactions', t, t.transactionId);
  }

  // Allocation history
  const history = (data.history ?? []).filter(h => h.userId === userId);
  for (const h of history) {
    await mergeRecord('allocationHistory', h, h.historyId);
  }

  // Monthly snapshots
  const snapshots = (data.snapshots ?? []).filter(s => s.userId === userId);
  for (const s of snapshots) {
    await mergeRecord('monthlySnapshots', s, [s.userId, s.yearMonth]);
  }

  return count;
}

/**
 * Restore data from the latest Drive backup.
 * Called once per session immediately after the user unlocks with their PIN.
 *
 * @param userId     - The authenticated user's UID.
 * @param masterKey  - The decryption key derived from the user's PIN.
 * @param driveToken - The Google Drive OAuth access token.
 */
export async function restoreFromDrive(
  userId: string,
  masterKey: CryptoKey,
): Promise<RestoreResult> {
  // 1. Download the encrypted blob from the server/Drive
  const download = await downloadBackup();

  if (download.noBackup || !download.encryptedBlob) {
    return { restored: false, recordCount: 0 };
  }

  // 2. Decrypt
  const payload = await decryptData<BackupPayload>(download.encryptedBlob, masterKey);
  // 3. Import into IndexedDB
  const recordCount = await importAllData(userId, payload);

  console.debug(`[Restore] Imported ${recordCount} records from ${download.backupPath}`);
  return { restored: true, recordCount, backupPath: download.backupPath };
}
